import { useEffect, useMemo, useState } from 'react'
import {
  Bell,
  Bot,
  Building2,
  Copy,
  Eye,
  EyeOff,
  Globe2,
  Key,
  Palette,
  RefreshCw,
  Tag,
  Trash2,
  Upload,
  UserCircle2,
} from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/common/Badge'
import { Button } from '@/components/common/Button'
import { Card, CardHeader, CardTitle } from '@/components/common/Card'
import { Input } from '@/components/common/Input'
import { PageSpinner } from '@/components/common/Spinner'
import { useAuth } from '@/hooks/useAuth'
import ApiTokensService from '@/services/api-tokens.service'
import CompaniesService from '@/services/companies.service'
import { notificationService } from '@/services/notification.service'
import TagsService from '@/services/tags.service'
import UsersService from '@/services/users.service'
import WebhookSettingsService from '@/services/webhook-settings.service'
import { useAuthStore } from '@/stores/authStore'
import { useBrandingStore } from '@/stores/brandingStore'
import type { Company, TenantTag, UserApiToken, WorkspaceWebhookSettings } from '@/types'
import { APP_NAME } from '@/utils/app-constants'
import { resolveMediaUrl } from '@/utils/media'

interface ProfileForm {
  full_name: string
}

interface TagForm {
  name: string
  color: string
}

interface WebhookForm {
  destination_url: string
  enabled: boolean
  token: string
  internal_webhook_url: string
}

interface ApiTokenForm {
  name: string
  expires_in_days: string
}

interface BrandingForm {
  platform_name: string
  logo_url: string
  icon_url: string
  primary_color: string
  secondary_color: string
  accent_color: string
}

interface OpenAIForm {
  api_key: string
}

interface GoogleForm {
  client_id: string
  client_secret: string
}

const emptyTagForm: TagForm = {
  name: '',
  color: '#6366F1',
}

const emptyWebhookForm: WebhookForm = {
  destination_url: '',
  enabled: false,
  token: '',
  internal_webhook_url: '',
}

const emptyApiTokenForm: ApiTokenForm = {
  name: '',
  expires_in_days: '',
}

const defaultBrandingForm: BrandingForm = {
  platform_name: APP_NAME,
  logo_url: '',
  icon_url: '',
  primary_color: '#2563EB',
  secondary_color: '#0F172A',
  accent_color: '#14B8A6',
}

const defaultOpenAIForm: OpenAIForm = {
  api_key: '',
}

const defaultGoogleForm: GoogleForm = {
  client_id: '',
  client_secret: '',
}

function readSettingsSection<T>(
  settings: Record<string, unknown> | undefined,
  key: string,
  fallback: T,
): T {
  const raw = settings?.[key]
  if (!raw || typeof raw !== 'object') return fallback
  return {
    ...(fallback as object),
    ...(raw as object),
  } as T
}

function maskSecret(value: string): string {
  if (!value) return ''
  if (value.length <= 8) return '•'.repeat(value.length)
  return `${value.slice(0, 4)}${'•'.repeat(Math.max(4, value.length - 8))}${value.slice(-4)}`
}

export default function SettingsPage() {
  const { user, isSuperAdmin, canAccessElement } = useAuth()
  const setUser = useAuthStore((state) => state.setUser)
  const setBranding = useBrandingStore((state) => state.setBranding)

  const [isLoading, setIsLoading] = useState(true)
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [isSavingNotifications, setIsSavingNotifications] = useState(false)
  const [isSavingTag, setIsSavingTag] = useState(false)
  const [isSavingWebhook, setIsSavingWebhook] = useState(false)
  const [isSavingBranding, setIsSavingBranding] = useState(false)
  const [isSavingOpenAI, setIsSavingOpenAI] = useState(false)
  const [isSavingGoogle, setIsSavingGoogle] = useState(false)
  const [isCreatingApiToken, setIsCreatingApiToken] = useState(false)
  const [isWebhookRegenerating, setIsWebhookRegenerating] = useState(false)
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null)
  const [tags, setTags] = useState<TenantTag[]>([])
  const [webhookSettings, setWebhookSettings] = useState<WorkspaceWebhookSettings | null>(null)
  const [apiTokens, setApiTokens] = useState<UserApiToken[]>([])
  const [createdApiToken, setCreatedApiToken] = useState<string | null>(null)
  const [editingTagId, setEditingTagId] = useState<string | null>(null)
  const [profileForm, setProfileForm] = useState<ProfileForm>({
    full_name: user?.full_name ?? '',
  })
  const [notificationsEnabled, setNotificationsEnabled] = useState(Boolean(user?.native_notifications_enabled))
  const [selectedAvatarFile, setSelectedAvatarFile] = useState<File | null>(null)
  const [tagForm, setTagForm] = useState<TagForm>(emptyTagForm)
  const [webhookForm, setWebhookForm] = useState<WebhookForm>(emptyWebhookForm)
  const [apiTokenForm, setApiTokenForm] = useState<ApiTokenForm>(emptyApiTokenForm)
  const [brandingForm, setBrandingForm] = useState<BrandingForm>(defaultBrandingForm)
  const [openAIForm, setOpenAIForm] = useState<OpenAIForm>(defaultOpenAIForm)
  const [googleForm, setGoogleForm] = useState<GoogleForm>(defaultGoogleForm)
  const [showWebhookToken, setShowWebhookToken] = useState(false)
  const [showOpenAIToken, setShowOpenAIToken] = useState(false)
  const [showGoogleSecret, setShowGoogleSecret] = useState(false)

  const canViewProfileBlock = canAccessElement('configuracoes.geral.perfil')
  const canViewTagsBlock = canAccessElement('configuracoes.geral.tags_customizadas')
  const canViewNotificationsBlock = canAccessElement('configuracoes.geral.notificacoes')
  const canViewApiTokensBlock = canAccessElement('configuracoes.geral.api_token_visualizar')
  const canViewCompanyBlock = canAccessElement('configuracoes.geral.company')
  const canViewCentralWebhookBlock = canAccessElement('configuracoes.geral.webhook_central')
  const canViewBrandingBlock = canAccessElement('configuracoes.geral.branding')
  const canViewOpenAIBlock = canAccessElement('configuracoes.geral.openai')
  const canViewGoogleBlock = canAccessElement('configuracoes.geral.google')

  const avatarPreview = useMemo(() => {
    if (selectedAvatarFile) return URL.createObjectURL(selectedAvatarFile)
    return resolveMediaUrl(user?.avatar_url) ?? ''
  }, [selectedAvatarFile, user?.avatar_url])

  useEffect(() => {
    return () => {
      if (selectedAvatarFile && avatarPreview.startsWith('blob:')) {
        URL.revokeObjectURL(avatarPreview)
      }
    }
  }, [avatarPreview, selectedAvatarFile])

  useEffect(() => {
    setProfileForm({ full_name: user?.full_name ?? '' })
    setNotificationsEnabled(Boolean(user?.native_notifications_enabled))
  }, [user?.full_name, user?.native_notifications_enabled])

  async function loadPage() {
    setIsLoading(true)
    try {
      const [company, tenantTags, tenantWebhook, tokens] = await Promise.all([
        isSuperAdmin ? CompaniesService.getCurrent() : Promise.resolve(null),
        canViewTagsBlock ? TagsService.getAll() : Promise.resolve([]),
        canViewCentralWebhookBlock ? WebhookSettingsService.get() : Promise.resolve(null),
        canViewApiTokensBlock ? ApiTokensService.list().catch(() => [] as UserApiToken[]) : Promise.resolve([] as UserApiToken[]),
      ])

      setCurrentCompany(company)
      setTags(tenantTags)
      setWebhookSettings(tenantWebhook)
      setWebhookForm({
        destination_url: tenantWebhook?.destination_url ?? '',
        enabled: Boolean(tenantWebhook?.enabled),
        token: tenantWebhook?.token ?? '',
        internal_webhook_url: tenantWebhook?.internal_webhook_url ?? '',
      })
      setApiTokens(tokens)

      if (company) {
        setBrandingForm(readSettingsSection(company.settings, 'branding', defaultBrandingForm))
        setOpenAIForm(readSettingsSection(company.settings, 'openai', defaultOpenAIForm))
        setGoogleForm(readSettingsSection(company.settings, 'google', defaultGoogleForm))
      }
    } catch {
      toast.error('Nao foi possivel carregar as configuracoes gerais.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadPage().catch(() => undefined)
  }, [canViewApiTokensBlock, canViewCentralWebhookBlock, canViewTagsBlock, isSuperAdmin])

  async function handleSaveProfile() {
    if (!user) return
    if (!profileForm.full_name.trim()) {
      toast.error('Informe o nome do usuario.')
      return
    }
    if (selectedAvatarFile) {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/webp']
      if (!allowedTypes.includes(selectedAvatarFile.type)) {
        toast.error('Formato de imagem invalido. Use PNG, JPG ou WEBP.')
        return
      }
      const maxSize = 2 * 1024 * 1024
      if (selectedAvatarFile.size > maxSize) {
        toast.error('A imagem excede o limite de 2 MB.')
        return
      }
    }

    setIsSavingProfile(true)
    try {
      let updatedUser = await UsersService.updateProfile({
        full_name: profileForm.full_name.trim(),
      })

      if (selectedAvatarFile) {
        updatedUser = await UsersService.uploadAvatar(selectedAvatarFile)
        setSelectedAvatarFile(null)
      }

      setUser(updatedUser)
      toast.success('Perfil atualizado com sucesso.')
    } catch {
      toast.error('Nao foi possivel atualizar o perfil.')
    } finally {
      setIsSavingProfile(false)
    }
  }

  async function handleSaveNotifications() {
    setIsSavingNotifications(true)
    try {
      const updatedUser = await UsersService.updatePreferences({
        native_notifications_enabled: notificationsEnabled,
      })
      setUser(updatedUser)
      toast.success('Preferencias de notificacao atualizadas.')
    } catch {
      toast.error('Nao foi possivel salvar as notificacoes.')
    } finally {
      setIsSavingNotifications(false)
    }
  }

  async function handleRequestNotificationsPermission() {
    const permission = await notificationService.requestPermission()
    if (permission === 'granted') {
      toast.success('Permissao do navegador liberada.')
      return
    }
    if (permission === 'unsupported') {
      toast.error('Seu navegador nao suporta notificacoes nativas.')
      return
    }
    toast.error('Permissao de notificacao nao concedida.')
  }

  async function handleSaveTag() {
    if (!tagForm.name.trim()) {
      toast.error('Informe o nome da tag.')
      return
    }

    setIsSavingTag(true)
    try {
      if (editingTagId) {
        const updated = await TagsService.update(editingTagId, {
          name: tagForm.name.trim(),
          color: tagForm.color,
        })
        setTags((current) => current.map((item) => (item.id === updated.id ? updated : item)))
        toast.success('Tag atualizada com sucesso.')
      } else {
        const created = await TagsService.create({
          name: tagForm.name.trim(),
          color: tagForm.color,
        })
        setTags((current) => [created, ...current])
        toast.success('Tag criada com sucesso.')
      }

      setEditingTagId(null)
      setTagForm(emptyTagForm)
    } catch {
      toast.error('Nao foi possivel salvar a tag.')
    } finally {
      setIsSavingTag(false)
    }
  }

  async function handleDeleteTag(tag: TenantTag) {
    try {
      await TagsService.delete(tag.id)
      setTags((current) => current.filter((item) => item.id !== tag.id))
      if (editingTagId === tag.id) {
        setEditingTagId(null)
        setTagForm(emptyTagForm)
      }
      toast.success(`Tag ${tag.name} removida.`)
    } catch {
      toast.error('Nao foi possivel remover a tag.')
    }
  }

  async function handleSaveWebhook() {
    setIsSavingWebhook(true)
    try {
      const updated = await WebhookSettingsService.update({
        enabled: webhookForm.enabled,
        destination_url: webhookForm.destination_url.trim() || null,
        token: webhookForm.token.trim() || null,
      })
      setWebhookSettings(updated)
      setWebhookForm({
        destination_url: updated.destination_url ?? '',
        enabled: Boolean(updated.enabled),
        token: updated.token ?? '',
        internal_webhook_url: updated.internal_webhook_url ?? '',
      })
      toast.success('Webhook central atualizado.')
    } catch {
      toast.error('Nao foi possivel salvar o webhook central.')
    } finally {
      setIsSavingWebhook(false)
    }
  }

  async function handleRegenerateWebhookToken() {
    setIsWebhookRegenerating(true)
    try {
      const updated = await WebhookSettingsService.regenerateToken()
      setWebhookSettings(updated)
      setWebhookForm({
        destination_url: updated.destination_url ?? '',
        enabled: Boolean(updated.enabled),
        token: updated.token ?? '',
        internal_webhook_url: updated.internal_webhook_url ?? '',
      })
      setShowWebhookToken(true)
      toast.success('Token do webhook regenerado.')
    } catch {
      toast.error('Nao foi possivel regenerar o token do webhook.')
    } finally {
      setIsWebhookRegenerating(false)
    }
  }

  async function handleCreateApiToken() {
    if (!apiTokenForm.name.trim()) {
      toast.error('Informe um nome para o token.')
      return
    }

    setIsCreatingApiToken(true)
    try {
      const created = await ApiTokensService.create({
        name: apiTokenForm.name.trim(),
        expires_in_days: apiTokenForm.expires_in_days ? Number(apiTokenForm.expires_in_days) : null,
      })
      setApiTokens((current) => [created.token, ...current])
      setCreatedApiToken(created.access_token)
      setApiTokenForm(emptyApiTokenForm)
      toast.success('Token de API criado com sucesso.')
    } catch {
      toast.error('Nao foi possivel criar o token.')
    } finally {
      setIsCreatingApiToken(false)
    }
  }

  async function handleRevokeApiToken(tokenId: string) {
    try {
      await ApiTokensService.revoke(tokenId)
      setApiTokens((current) => current.filter((item) => item.id !== tokenId))
      toast.success('Token revogado com sucesso.')
    } catch {
      toast.error('Nao foi possivel revogar o token.')
    }
  }

  async function handleCopy(value: string, successMessage: string) {
    try {
      await navigator.clipboard.writeText(value)
      toast.success(successMessage)
    } catch {
      toast.error('Nao foi possivel copiar para a area de transferencia.')
    }
  }

  async function handleSaveBranding() {
    if (!brandingForm.platform_name || brandingForm.platform_name.trim().length < 2) {
      toast.error('Nome da plataforma invalido.')
      return
    }

    setIsSavingBranding(true)
    try {
      const updated = await CompaniesService.updateBranding({
        platform_name: brandingForm.platform_name.trim(),
        logo_url: brandingForm.logo_url.trim() || null,
        icon_url: brandingForm.icon_url.trim() || null,
        primary_color: brandingForm.primary_color,
        secondary_color: brandingForm.secondary_color,
        accent_color: brandingForm.accent_color,
      })
      setCurrentCompany(updated)
      const nextBranding = readSettingsSection(updated.settings, 'branding', defaultBrandingForm)
      setBrandingForm(nextBranding)
      setBranding(nextBranding)
      toast.success('Branding atualizado com sucesso.')
    } catch {
      toast.error('Nao foi possivel salvar o branding.')
    } finally {
      setIsSavingBranding(false)
    }
  }

  async function handleSaveOpenAI() {
    if (!openAIForm.api_key.trim()) {
      toast.error('Informe o token da OpenAI.')
      return
    }

    setIsSavingOpenAI(true)
    try {
      const updated = await CompaniesService.updateOpenAISettings({
        api_key: openAIForm.api_key.trim(),
      })
      setCurrentCompany(updated)
      setOpenAIForm(readSettingsSection(updated.settings, 'openai', defaultOpenAIForm))
      toast.success('Token OpenAI atualizado.')
    } catch {
      toast.error('Nao foi possivel salvar o token OpenAI.')
    } finally {
      setIsSavingOpenAI(false)
    }
  }

  async function handleSaveGoogle() {
    if (!googleForm.client_id.trim() || !googleForm.client_secret.trim()) {
      toast.error('Informe Client ID e Client Secret do Google.')
      return
    }

    setIsSavingGoogle(true)
    try {
      const updated = await CompaniesService.updateGoogleSettings({
        client_id: googleForm.client_id.trim(),
        client_secret: googleForm.client_secret.trim(),
      })
      setCurrentCompany(updated)
      setGoogleForm(readSettingsSection(updated.settings, 'google', defaultGoogleForm))
      toast.success('Credenciais Google atualizadas.')
    } catch {
      toast.error('Nao foi possivel salvar as credenciais Google.')
    } finally {
      setIsSavingGoogle(false)
    }
  }

  if (isLoading) {
    return <PageSpinner label="Carregando configuracoes..." />
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1>Configuracoes gerais</h1>
        <p className="text-sm text-[var(--text-muted)]">
          Ajuste perfil, operacao do tenant atual e integracoes sensiveis conforme o seu nivel de acesso.
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        {canViewProfileBlock && (
          <Card className="space-y-5">
            <CardHeader className="mb-0">
              <div className="space-y-1">
                <CardTitle>Perfil do usuario</CardTitle>
                <p className="text-sm text-[var(--text-muted)]">Atualize seu nome e a foto usada no sistema.</p>
              </div>
              <Badge variant="brand">Todos os perfis</Badge>
            </CardHeader>

            <div className="flex items-start gap-4">
              <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border border-[var(--border-medium)] bg-[var(--glass-light)]">
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="Avatar do usuario"
                    onError={(event) => {
                      event.currentTarget.src = '/default-avatar.svg'
                    }}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <UserCircle2 size={34} className="text-[var(--text-muted)]" />
                )}
              </div>

              <div className="flex-1 space-y-3">
                <Input
                  label="Nome"
                  value={profileForm.full_name}
                  onChange={(event) => setProfileForm({ full_name: event.target.value })}
                />
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-[var(--text-primary)]">Nova foto</label>
                  <label className="flex h-11 cursor-pointer items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border-medium)] bg-[var(--glass-light)] px-4 text-sm text-[var(--text-secondary)] transition hover:text-[var(--text-primary)]">
                    <Upload size={15} />
                    <span>{selectedAvatarFile?.name ?? 'Selecionar imagem'}</span>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      onChange={(event) => setSelectedAvatarFile(event.target.files?.[0] ?? null)}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button loading={isSavingProfile} onClick={() => handleSaveProfile().catch(() => undefined)}>
                Salvar perfil
              </Button>
            </div>
          </Card>
        )}

        {canViewNotificationsBlock && (
          <Card className="space-y-5">
            <CardHeader className="mb-0">
              <div className="space-y-1">
                <CardTitle>Preferencias de notificacao</CardTitle>
                <p className="text-sm text-[var(--text-muted)]">Controle alertas locais do navegador para novas interacoes.</p>
              </div>
              <Badge variant="info">
                <Bell size={12} />
                {notificationService.getPermission()}
              </Badge>
            </CardHeader>

            <label className="flex items-center justify-between gap-4 rounded-[var(--radius-md)] border border-[var(--border-medium)] bg-[var(--glass-light)] px-4 py-3">
              <div>
                <p className="font-medium text-[var(--text-primary)]">Notificacoes nativas</p>
                <p className="text-sm text-[var(--text-muted)]">Receber alertas no dispositivo quando houver atividade relevante.</p>
              </div>
              <input
                type="checkbox"
                className="h-5 w-5 accent-[var(--color-brand)]"
                checked={notificationsEnabled}
                onChange={(event) => setNotificationsEnabled(event.target.checked)}
              />
            </label>

            <div className="flex flex-wrap justify-end gap-3">
              <Button variant="outline" onClick={() => handleRequestNotificationsPermission().catch(() => undefined)}>
                Solicitar permissao
              </Button>
              <Button loading={isSavingNotifications} onClick={() => handleSaveNotifications().catch(() => undefined)}>
                Salvar notificacoes
              </Button>
            </div>
          </Card>
        )}

        {canViewTagsBlock && (
          <Card className="space-y-5">
            <CardHeader className="mb-0">
              <div className="space-y-1">
                <CardTitle>Tags customizadas</CardTitle>
                <p className="text-sm text-[var(--text-muted)]">Crie, edite e remova tags para organizar contatos e operacoes.</p>
              </div>
              <Badge variant="brand">
                <Tag size={12} />
                {tags.length} tags
              </Badge>
            </CardHeader>

            <div className="grid gap-4 md:grid-cols-[1.4fr,140px,auto]">
              <Input
                label="Nome da tag"
                value={tagForm.name}
                onChange={(event) => setTagForm((current) => ({ ...current, name: event.target.value }))}
              />
              <Input
                label="Cor"
                type="color"
                className="h-11 px-2"
                value={tagForm.color}
                onChange={(event) => setTagForm((current) => ({ ...current, color: event.target.value }))}
              />
              <div className="flex items-end gap-3">
                <Button loading={isSavingTag} onClick={() => handleSaveTag().catch(() => undefined)}>
                  {editingTagId ? 'Salvar edicao' : 'Criar tag'}
                </Button>
                {editingTagId && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setEditingTagId(null)
                      setTagForm(emptyTagForm)
                    }}
                  >
                    Cancelar
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-3">
              {tags.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)]">Nenhuma tag criada ainda.</p>
              ) : (
                tags.map((tag) => (
                  <div
                    key={tag.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-md)] border border-[var(--border-medium)] bg-[var(--glass-light)] px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <span className="h-3 w-3 rounded-full" style={{ backgroundColor: tag.color }} />
                      <span className="font-medium text-[var(--text-primary)]">{tag.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingTagId(tag.id)
                          setTagForm({ name: tag.name, color: tag.color })
                        }}
                      >
                        Editar
                      </Button>
                      <Button variant="danger" size="sm" icon={<Trash2 size={14} />} onClick={() => handleDeleteTag(tag).catch(() => undefined)}>
                        Remover
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        )}

        {canViewApiTokensBlock && (
          <Card className="space-y-5">
            <CardHeader className="mb-0">
              <div className="space-y-1">
                <CardTitle>Token API</CardTitle>
                <p className="text-sm text-[var(--text-muted)]">Gerencie tokens pessoais para integrações autenticadas da tenant atual.</p>
              </div>
              <Badge variant="brand">
                <Key size={12} />
                {apiTokens.length} tokens
              </Badge>
            </CardHeader>

            <div className="grid gap-4 md:grid-cols-[1.3fr,180px,auto]">
              <Input
                label="Nome do token"
                value={apiTokenForm.name}
                onChange={(event) => setApiTokenForm((current) => ({ ...current, name: event.target.value }))}
              />
              <Input
                label="Expira em dias"
                type="number"
                min={1}
                value={apiTokenForm.expires_in_days}
                onChange={(event) => setApiTokenForm((current) => ({ ...current, expires_in_days: event.target.value }))}
              />
              <div className="flex items-end">
                <Button loading={isCreatingApiToken} onClick={() => handleCreateApiToken().catch(() => undefined)}>
                  Gerar token
                </Button>
              </div>
            </div>

            {createdApiToken && (
              <div className="space-y-2 rounded-[var(--radius-md)] border border-[rgba(var(--color-brand-rgb),0.22)] bg-[rgba(var(--color-brand-rgb),0.08)] p-4">
                <p className="text-sm font-medium text-[var(--text-primary)]">Token gerado agora</p>
                <div className="flex flex-wrap items-center gap-3">
                  <code className="rounded bg-[var(--glass-light)] px-3 py-2 text-xs text-[var(--text-primary)]">{createdApiToken}</code>
                  <Button variant="outline" size="sm" icon={<Copy size={14} />} onClick={() => handleCopy(createdApiToken, 'Token copiado.')}>
                    Copiar
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {apiTokens.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)]">Nenhum token gerado ainda.</p>
              ) : (
                apiTokens.map((token) => (
                  <div
                    key={token.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-md)] border border-[var(--border-medium)] bg-[var(--glass-light)] px-4 py-3"
                  >
                    <div className="space-y-1">
                      <p className="font-medium text-[var(--text-primary)]">{token.name}</p>
                      <p className="text-sm text-[var(--text-muted)]">
                        Prefixo {token.prefix} • criado em {new Date(token.created_at).toLocaleString('pt-BR')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={token.revoked_at ? 'error' : 'success'}>
                        {token.revoked_at ? 'Revogado' : 'Ativo'}
                      </Badge>
                      {!token.revoked_at && (
                        <Button variant="danger" size="sm" onClick={() => handleRevokeApiToken(token.id).catch(() => undefined)}>
                          Revogar
                        </Button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        )}

        {canViewCompanyBlock && currentCompany && (
          <Card className="space-y-5">
            <CardHeader className="mb-0">
              <div className="space-y-1">
                <CardTitle>Tenant atual</CardTitle>
                <p className="text-sm text-[var(--text-muted)]">Resumo administrativo da company ativa do workspace.</p>
              </div>
              <Badge variant={currentCompany.is_active ? 'success' : 'error'}>
                <Building2 size={12} />
                {currentCompany.is_active ? 'Ativa' : 'Inativa'}
              </Badge>
            </CardHeader>

            <div className="grid gap-4 md:grid-cols-2">
              <Input label="Nome" value={currentCompany.name} readOnly />
              <Input label="Slug" value={currentCompany.slug} readOnly />
              <Input label="Razao social" value={currentCompany.legal_name ?? ''} readOnly />
              <Input label="Admin ancora" value={currentCompany.admin_email ?? ''} readOnly />
            </div>
          </Card>
        )}

        {canViewCentralWebhookBlock && (
          <Card className="space-y-5">
            <CardHeader className="mb-0">
              <div className="space-y-1">
                <CardTitle>Webhook central do tenant</CardTitle>
                <p className="text-sm text-[var(--text-muted)]">Configura o encaminhamento externo consolidado do workspace.</p>
              </div>
              <Badge variant={webhookSettings?.enabled ? 'success' : 'default'}>
                {webhookSettings?.enabled ? 'Ativo' : 'Inativo'}
              </Badge>
            </CardHeader>

            <div className="grid gap-4">
              <Input
                label="URL interna do sistema"
                value={webhookForm.internal_webhook_url}
                readOnly
                rightIcon={<Copy size={14} />}
                onRightIconClick={
                  webhookForm.internal_webhook_url
                    ? () => handleCopy(webhookForm.internal_webhook_url, 'URL interna copiada.')
                    : undefined
                }
              />
              <Input
                label="URL de destino externo"
                value={webhookForm.destination_url}
                onChange={(event) => setWebhookForm((current) => ({ ...current, destination_url: event.target.value }))}
              />
              <Input
                label="Token Bearer"
                value={showWebhookToken ? webhookForm.token : maskSecret(webhookForm.token)}
                readOnly={!showWebhookToken}
                onChange={(event) => setWebhookForm((current) => ({ ...current, token: event.target.value }))}
                rightIcon={showWebhookToken ? <EyeOff size={14} /> : <Eye size={14} />}
                onRightIconClick={() => setShowWebhookToken((current) => !current)}
              />
              <label className="flex items-center justify-between gap-4 rounded-[var(--radius-md)] border border-[var(--border-medium)] bg-[var(--glass-light)] px-4 py-3">
                <div>
                  <p className="font-medium text-[var(--text-primary)]">Encaminhamento externo ativo</p>
                  <p className="text-sm text-[var(--text-muted)]">Habilita o disparo do webhook central para eventos suportados.</p>
                </div>
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-[var(--color-brand)]"
                  checked={webhookForm.enabled}
                  onChange={(event) => setWebhookForm((current) => ({ ...current, enabled: event.target.checked }))}
                />
              </label>
            </div>

            <div className="flex flex-wrap justify-end gap-3">
              <Button
                variant="outline"
                loading={isWebhookRegenerating}
                icon={<RefreshCw size={14} />}
                onClick={() => handleRegenerateWebhookToken().catch(() => undefined)}
              >
                Regenerar token
              </Button>
              <Button loading={isSavingWebhook} onClick={() => handleSaveWebhook().catch(() => undefined)}>
                Salvar webhook
              </Button>
            </div>
          </Card>
        )}

        {canViewBrandingBlock && (
          <Card className="space-y-5">
            <CardHeader className="mb-0">
              <div className="space-y-1">
                <CardTitle>Branding da plataforma</CardTitle>
                <p className="text-sm text-[var(--text-muted)]">Define nome, ativos visuais e paleta principal da interface.</p>
              </div>
              <Badge variant="brand">
                <Palette size={12} />
                Superadmin
              </Badge>
            </CardHeader>

            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Nome da plataforma"
                value={brandingForm.platform_name}
                onChange={(event) => setBrandingForm((current) => ({ ...current, platform_name: event.target.value }))}
              />
              <Input
                label="Logo URL"
                value={brandingForm.logo_url}
                onChange={(event) => setBrandingForm((current) => ({ ...current, logo_url: event.target.value }))}
              />
              <Input
                label="Icone URL"
                value={brandingForm.icon_url}
                onChange={(event) => setBrandingForm((current) => ({ ...current, icon_url: event.target.value }))}
              />
              <div className="grid grid-cols-3 gap-3">
                <Input
                  label="Cor primaria"
                  type="color"
                  className="h-11 px-2"
                  value={brandingForm.primary_color}
                  onChange={(event) => setBrandingForm((current) => ({ ...current, primary_color: event.target.value }))}
                />
                <Input
                  label="Cor secundaria"
                  type="color"
                  className="h-11 px-2"
                  value={brandingForm.secondary_color}
                  onChange={(event) => setBrandingForm((current) => ({ ...current, secondary_color: event.target.value }))}
                />
                <Input
                  label="Cor destaque"
                  type="color"
                  className="h-11 px-2"
                  value={brandingForm.accent_color}
                  onChange={(event) => setBrandingForm((current) => ({ ...current, accent_color: event.target.value }))}
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button loading={isSavingBranding} onClick={() => handleSaveBranding().catch(() => undefined)}>
                Salvar branding
              </Button>
            </div>
          </Card>
        )}

        {canViewOpenAIBlock && (
          <Card className="space-y-5">
            <CardHeader className="mb-0">
              <div className="space-y-1">
                <CardTitle>Integracao OpenAI</CardTitle>
                <p className="text-sm text-[var(--text-muted)]">Armazena o token base usado nas integrações globais de IA.</p>
              </div>
              <Badge variant="warning">
                <Bot size={12} />
                Sensivel
              </Badge>
            </CardHeader>

            <Input
              label="Token OpenAI"
              type={showOpenAIToken ? 'text' : 'password'}
              value={openAIForm.api_key}
              onChange={(event) => setOpenAIForm({ api_key: event.target.value })}
              rightIcon={showOpenAIToken ? <EyeOff size={14} /> : <Eye size={14} />}
              onRightIconClick={() => setShowOpenAIToken((current) => !current)}
            />

            <div className="flex justify-end">
              <Button loading={isSavingOpenAI} onClick={() => handleSaveOpenAI().catch(() => undefined)}>
                Salvar token
              </Button>
            </div>
          </Card>
        )}

        {canViewGoogleBlock && (
          <Card className="space-y-5">
            <CardHeader className="mb-0">
              <div className="space-y-1">
                <CardTitle>Credenciais Google</CardTitle>
                <p className="text-sm text-[var(--text-muted)]">Guarda as credenciais usadas por integrações corporativas do Google.</p>
              </div>
              <Badge variant="warning">
                <Globe2 size={12} />
                Sensivel
              </Badge>
            </CardHeader>

            <div className="grid gap-4">
              <Input
                label="Google Client ID"
                value={googleForm.client_id}
                onChange={(event) => setGoogleForm((current) => ({ ...current, client_id: event.target.value }))}
              />
              <Input
                label="Google Client Secret"
                type={showGoogleSecret ? 'text' : 'password'}
                value={googleForm.client_secret}
                onChange={(event) => setGoogleForm((current) => ({ ...current, client_secret: event.target.value }))}
                rightIcon={showGoogleSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                onRightIconClick={() => setShowGoogleSecret((current) => !current)}
              />
            </div>

            <div className="flex justify-end">
              <Button loading={isSavingGoogle} onClick={() => handleSaveGoogle().catch(() => undefined)}>
                Salvar credenciais
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}
